import { createContext, Suspense, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { lazyPage } from '../lib/lazyPage';

const HelpPanel = lazyPage(() => import('../components/HelpPanel'));

export interface HelpApi {
  /** Opens the help panel, on `articleId` when given (otherwise on the last-viewed article or the home list). */
  openHelp: (articleId?: string) => void;
  closeHelp: () => void;
  isOpen: boolean;
}

const noop: HelpApi = { openHelp: () => {}, closeHelp: () => {}, isOpen: false };
const HelpContext = createContext<HelpApi>(noop);

export function HelpProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  // `n` makes re-opening the same article a new request, so the panel re-navigates.
  const [request, setRequest] = useState<{ id?: string; n: number }>({ n: 0 });

  const openHelp = useCallback((articleId?: string) => {
    setRequest((r) => ({ id: articleId, n: r.n + 1 }));
    setHasOpened(true);
    setIsOpen(true);
  }, []);
  const closeHelp = useCallback(() => setIsOpen(false), []);

  const api = useMemo<HelpApi>(() => ({ openHelp, closeHelp, isOpen }), [openHelp, closeHelp, isOpen]);

  return (
    <HelpContext.Provider value={api}>
      {children}
      {hasOpened && (
        <Suspense fallback={null}>
          <HelpPanel open={isOpen} onClose={closeHelp} articleId={request.id} requestId={request.n} />
        </Suspense>
      )}
    </HelpContext.Provider>
  );
}

export function useHelp(): HelpApi {
  return useContext(HelpContext);
}
